import { Suspense, useEffect, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AuthProvider } from './auth/AuthProvider'
import { useAuth } from './auth/AuthProvider'
import { homeCabinet } from './auth/homeCabinet'
import { clearReturnFromSignIn, isReturnFromSignIn } from './auth/keycloak'
import { isStandaloneDisplay } from './lib/pwaInstall'
import AppLayout from './components/layout/AppLayout'
import NotFoundRedirect from './components/NotFoundRedirect'
import AuthGuard from './components/AuthGuard'
import CookieNotice from './components/CookieNotice'
import Metrika from './components/Metrika'
import PwaUpdateBanner from './components/PwaUpdateBanner'
import AttributionSync from './components/AttributionSync'
import ConsentGate from './components/ConsentGate'
import LegalUpdateNotice from './components/LegalUpdateNotice'
import OpenInAppBanner from './components/OpenInAppBanner'
import DriverBrowserNotice from './components/DriverBrowserNotice'
import DriverInviteWelcomeGate from './components/DriverInviteWelcomeGate'
import { ErrorBoundary } from './components/ErrorBoundary'
import { useMobileViewport } from './hooks/useMobileViewport'
import SEOHead from './components/SEOHead'
import PublicPrerenderHandoff from './components/PublicPrerenderHandoff'
import { lazyWithReload } from './lib/lazyWithReload'
import { ROUTE_PLANNING_ENABLED } from './lib/features'
import { INFORMATION_ROUTES_ENABLED, SOLUTIONS_PREVIEW_ENABLED } from './lib/solutionsPreview'
import FeedbackHost from './components/feedback/FeedbackHost'
import NotificationClickNavigator from './components/NotificationClickNavigator'

const DriverAccept = lazyWithReload(() => import('./pages/DriverAccept'))
const DriverDashboard = lazyWithReload(() => import('./pages/DriverDashboard'))
const DriverDocuments = lazyWithReload(() => import('./pages/DriverDocuments'))
const DriverTracking = lazyWithReload(() => import('./pages/DriverTracking'))
const ClientPublicTrack = lazyWithReload(() => import('./pages/ClientPublicTrack'))
const DriverHistory = lazyWithReload(() => import('./pages/DriverHistory'))
const ManagerPricing = lazyWithReload(() => import('./pages/ManagerPricing'))
const ManagerAnalytics = lazyWithReload(() => import('./pages/ManagerAnalytics'))
const ManagerRoutePlanning = lazyWithReload(() => import('./pages/ManagerRoutePlanning'))
const DriverProfile = lazyWithReload(() => import('./pages/DriverProfile'))
const ManagerProfile = lazyWithReload(() => import('./pages/ManagerProfile'))
const ManagerDashboard = lazyWithReload(() => import('./pages/ManagerDashboard'))
const ManagerHistory = lazyWithReload(() => import('./pages/ManagerHistory'))
const SavedTrackingLinks = lazyWithReload(() => import('./pages/SavedTrackingLinks'))
const LogistStoryLanding = lazyWithReload(() => import('./pages/LogistStoryLanding'))
const SolutionsPreview = INFORMATION_ROUTES_ENABLED
  ? lazyWithReload(() => import('./pages/solutions/SolutionsPreview'))
  : null
const InformationPreview = INFORMATION_ROUTES_ENABLED
  ? lazyWithReload(() => import('./pages/information/InformationPreview'))
  : null
const LogistInviteWelcome = lazyWithReload(() => import('./pages/LogistInviteWelcome'))
const Register = lazyWithReload(() => import('./pages/Register'))
const EmailConfirmed = lazyWithReload(() => import('./pages/EmailConfirmed'))
const ForgotPassword = lazyWithReload(() => import('./pages/ForgotPassword'))
const PasswordResetComplete = lazyWithReload(() => import('./pages/PasswordResetComplete'))
const EmailChangeComplete = lazyWithReload(() => import('./pages/EmailChangeComplete'))
const NotFound = lazyWithReload(() => import('./pages/NotFound'))
const LegalDocument = lazyWithReload(() => import('./pages/LegalDocument'))
const InternalDriverFunnel = lazyWithReload(() => import('./pages/InternalDriverFunnel'))
const InternalGrowth = lazyWithReload(() => import('./pages/InternalGrowth'))
const MobileManagerCabinet = lazyWithReload(() => import('./pages/mobile-web/MobileManagerCabinet'))
const MobileDriverCabinet = lazyWithReload(() => import('./pages/mobile-web/MobileDriverCabinet'))
const MobileDriverInvite = lazyWithReload(() => import('./pages/mobile-web/MobileDriverInvite'))
const MobileProfilePage = lazyWithReload(() => import('./pages/mobile-web/MobileUtilityPages').then((module) => ({ default: module.MobileProfilePage })))
const MobileSavedLinksPage = lazyWithReload(() => import('./pages/mobile-web/MobileUtilityPages').then((module) => ({ default: module.MobileSavedLinksPage })))
const MobilePricingPage = lazyWithReload(() => import('./pages/mobile-web/MobileUtilityPages').then((module) => ({ default: module.MobilePricingPage })))
const MobileAnalyticsPage = lazyWithReload(() => import('./pages/mobile-web/MobileUtilityPages').then((module) => ({ default: module.MobileAnalyticsPage })))

function ResponsiveCabinetPage({ mobile, desktop }: { mobile: ReactNode; desktop: ReactNode }) {
  return useMobileViewport() ? mobile : desktop
}

function ResponsiveDriverInvitePage() {
  return useMobileViewport() ? <MobileDriverInvite /> : <DriverAccept />
}

function TechnicalPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <SEOHead
        title={title}
        description="Внутренняя страница сервиса Лупа."
        robots="noindex, nofollow"
      />
      {children}
    </>
  )
}

function HomeRouter() {
  const { authenticated, activeRole } = useAuth()
  const dest = homeCabinet({
    authenticated,
    activeRole,
    standalone: isStandaloneDisplay(),
    returnedFromSignIn: isReturnFromSignIn(),
  })
  // The redirect is decided on the render that saw the session; after it, the root is the
  // landing again for the rest of the tab's life.
  useEffect(() => {
    if (authenticated) clearReturnFromSignIn()
  }, [authenticated])
  if (dest) return <Navigate to={dest} replace />
  return <LogistStoryLanding />
}

function ProfileRouter() {
  const { activeRole } = useAuth()

  if (useMobileViewport()) {
    return <MobileProfilePage />
  }

  if (activeRole === 'manager') {
    return <ManagerProfile />
  }

  return <DriverProfile />
}

function LegacySolutionRedirect() {
  const { search, hash } = useLocation()
  return <Navigate to={SOLUTIONS_PREVIEW_ENABLED ? `/solutions/otslezhivanie-bez-gps${search}${hash}` : '/landing'} replace />
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <FeedbackHost />
        <NotificationClickNavigator />
        <Metrika />
        <AppLayout>
          <Suspense fallback={null}>
          <PublicPrerenderHandoff>
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<HomeRouter />} />
              <Route path="/landing" element={<LogistStoryLanding />} />
              <Route path="/register" element={<TechnicalPage title="Регистрация — Лупа"><Register /></TechnicalPage>} />
              <Route path="/email-confirmed" element={<TechnicalPage title="Подтверждение email — Лупа"><EmailConfirmed /></TechnicalPage>} />
              <Route path="/forgot-password" element={<TechnicalPage title="Восстановление пароля — Лупа"><ForgotPassword /></TechnicalPage>} />
              <Route path="/password-reset-complete" element={<TechnicalPage title="Пароль изменён — Лупа"><PasswordResetComplete /></TechnicalPage>} />
              <Route path="/email-change-complete" element={<TechnicalPage title="Email изменён — Лупа"><EmailChangeComplete /></TechnicalPage>} />
              {SolutionsPreview && <Route path="/solutions/*" element={<SolutionsPreview />} />}
              {InformationPreview && <>
                <Route path="/pricing" element={<InformationPreview />} />
                <Route path="/for-drivers" element={<InformationPreview />} />
                <Route path="/about" element={<InformationPreview />} />
                <Route path="/articles/*" element={<InformationPreview />} />
              </>}
              <Route path="/otslezhivanie-bez-gps" element={<LegacySolutionRedirect />} />
              <Route path="/for-logists" element={<LegacySolutionRedirect />} />
              {/* A driver's link to a logist (lib/logistInvite.ts). */}
              <Route path="/ot-voditelya" element={<LogistInviteWelcome />} />
              <Route path="/legal/:type" element={<LegalDocument />} />
              <Route path="/tracking" element={
                <TechnicalPage title="Отслеживание — Лупа">
                  <AuthGuard>
                    <ResponsiveCabinetPage mobile={<MobileManagerCabinet mode="transit" />} desktop={<DriverTracking />} />
                  </AuthGuard>
                </TechnicalPage>
              } />

              <Route path="/manager" element={
                <TechnicalPage title="Заявки — Лупа">
                  <AuthGuard requiredRole="manager">
                    <ResponsiveCabinetPage mobile={<MobileManagerCabinet mode="home" />} desktop={<ManagerDashboard />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/manager/orders" element={
                <TechnicalPage title="Заявки — Лупа">
                  <AuthGuard requiredRole="manager">
                    <ResponsiveCabinetPage mobile={<MobileManagerCabinet mode="orders" />} desktop={<Navigate to="/manager" replace />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/manager/calendar" element={
                <TechnicalPage title="Календарь — Лупа">
                  <AuthGuard requiredRole="manager">
                    <ResponsiveCabinetPage mobile={<MobileManagerCabinet mode="calendar" />} desktop={<Navigate to="/manager" replace />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/manager/history" element={
                <TechnicalPage title="История заявок — Лупа">
                  <AuthGuard requiredRole="manager">
                    <ResponsiveCabinetPage mobile={<MobileManagerCabinet mode="history" />} desktop={<ManagerHistory />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/manager/pricing" element={
                <TechnicalPage title="Тарифы — Лупа">
                  <AuthGuard requiredRole="manager">
                    <ResponsiveCabinetPage mobile={<MobilePricingPage />} desktop={<ManagerPricing />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/manager/analytics" element={
                <TechnicalPage title="Аналитика — Лупа">
                  <AuthGuard requiredRole="manager">
                    <ResponsiveCabinetPage mobile={<MobileAnalyticsPage />} desktop={<ManagerAnalytics />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              {/* Planning 60 points on a phone is not a thing: desktop only (see PARITY.md). Not
                  behind ResponsiveCabinetPage: narrowing the window (half a laptop screen) must
                  not throw the logist's list away — the page itself says it wants more room. */}
              {ROUTE_PLANNING_ENABLED ? (
                <Route path="/manager/route-planning" element={
                  <TechnicalPage title="Маршрутизация — Лупа">
                    <AuthGuard requiredRole="manager">
                      <ManagerRoutePlanning />
                    </AuthGuard>
                  </TechnicalPage>
                } />
              ) : null}
              <Route path="/profile" element={
                <TechnicalPage title="Профиль — Лупа">
                  <AuthGuard>
                    <ProfileRouter />
                  </AuthGuard>
                </TechnicalPage>
              } />
              {/* Saved public tracking links — available to any authenticated user, since both
                  managers and drivers can receive a share link from someone else. */}
              <Route path="/saved-links" element={
                <TechnicalPage title="Полученные ссылки — Лупа">
                  <AuthGuard>
                    <ResponsiveCabinetPage mobile={<MobileSavedLinksPage />} desktop={<SavedTrackingLinks />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/driver" element={
                <TechnicalPage title="Заявки — Лупа">
                  <AuthGuard requiredRole="driver">
                    <ResponsiveCabinetPage mobile={<MobileDriverCabinet mode="home" />} desktop={<DriverDashboard />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/driver/trips" element={
                <TechnicalPage title="Заявки — Лупа">
                  <AuthGuard requiredRole="driver">
                    <ResponsiveCabinetPage mobile={<MobileDriverCabinet mode="trips" />} desktop={<Navigate to="/driver" replace />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/driver/documents" element={
                <TechnicalPage title="Документы — Лупа">
                  <AuthGuard requiredRole="driver">
                    <ResponsiveCabinetPage mobile={<MobileDriverCabinet mode="documents" />} desktop={<DriverDocuments />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/driver/history" element={
                <TechnicalPage title="История заявок — Лупа">
                  <AuthGuard requiredRole="driver">
                    <ResponsiveCabinetPage mobile={<MobileDriverCabinet mode="history" />} desktop={<DriverHistory />} />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/driver/accept" element={
                <TechnicalPage title="Заявка — Лупа">
                  <DriverInviteWelcomeGate>
                    <OpenInAppBanner />
                    <DriverBrowserNotice />
                    <ResponsiveDriverInvitePage />
                  </DriverInviteWelcomeGate>
                </TechnicalPage>
              } />
              {/* Product owners only — the backend answers 404 to everyone else. */}
              <Route path="/internal/driver-funnel" element={
                <TechnicalPage title="Воронка водителя — Лупа">
                  <AuthGuard>
                    <InternalDriverFunnel />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/internal/growth" element={
                <TechnicalPage title="Метрики роста — Лупа">
                  <AuthGuard>
                    <InternalGrowth />
                  </AuthGuard>
                </TechnicalPage>
              } />
              <Route path="/track/:id" element={<ClientPublicTrack />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFoundRedirect />} />
            </Routes>
          </ErrorBoundary>
          </PublicPrerenderHandoff>
          </Suspense>
        </AppLayout>
        <CookieNotice />
        <PwaUpdateBanner />
        <AttributionSync />
        {/* п. 8.1 соглашения: о новой редакции документов пользователя уведомляют, но не
            блокируют — продолжение использования и есть принятие. */}
        <LegalUpdateNotice />
        {/* 152-ФЗ: accounts created by social login never saw the registration form's consent
            checkbox, so they are asked here before anything of theirs is processed; the same
            gate comes up if the substance of the consent changed after they accepted. */}
        <ConsentGate />
      </BrowserRouter>
    </AuthProvider>
  )
}
